// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import type { Category } from "@tmi/types/entities";
import { refreshCategoriesFromServer } from "@/services/data/sync/category-sync-coordinator";
import LocalForagePlugin, { categories } from "@/plugins/LocalForagePlugin";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allCategories.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const defaultCategories = [] as unknown as Category[];
const allCategories = ref< Category[] >( defaultCategories );

export const useCategoriesStore = defineStore( 'categories', () => {
    function reset(): void {
        allCategories.value.splice( 0 );
        loading.value = false;
        refreshing.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Categories ].initialize() exiting - already processed...' );
            return;
        }

        logger.debug( `[ Categories ] Checking the local database for any categories...` );

        loading.value = true;

        const t0: number = performance.now();

        // Wait for the driver to settle — unconditionally. The bindings are assigned synchronously,
        // but an instance's own ready() resolves before sibling stores finish upgrading the shared
        // database on a fresh origin; reading in that window hangs localforage (fresh-origin boot race).
        await LocalForagePlugin.initialize();

        await categories.ready();
        const numberCategories = await categories.length();

        const uniqueCategories: Map< string, Category > = new Map< string, Category >();
        if ( numberCategories > 0 ) {
            // Clear the contents of the collections
            allCategories.value.splice( 0 );

            // Iterate over all elements within IDB
            categories.iterate( ( category: Category ) => {
                uniqueCategories.set( category.id, category );
                return;
            } ).then( () => {
                allCategories.value = [ ...( Array.from( uniqueCategories.values() ) ) ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allCategories.value.length } categories in ${ t1 - t0 } ms` );
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.message ) );
                reset();
            } ).finally( () => {
                // Clear the "loading" flag
                loading.value = false;
                refreshing.value = false;
            } );
        } else {
            if ( refreshing.value ) {
                return;
            }

            refreshing.value = true;

            // Timing - testing to see how expensive each method is
            const t0: number = performance.now();

            refreshCategoriesFromServer()
                .then( ( receivedCategories: Category[] ) => allCategories.value = [ ...receivedCategories ] )
                .catch( ( err: any ) => {
                    logger.error( JSON.stringify( err.message ) );
                    reset();
                } )
                .finally( () => {
                    // Clear the "loading" flag
                    loading.value = false;
                    refreshing.value = false;

                    const t1: number = performance.now();
                    logger.info( `Fetching & mapping ${ allCategories.value.length } category items took ${ t1 - t0 } ms` );
                } );
        }
    }

    function categoryById( sysId: string ) : Category | undefined {
        return allCategories.value.find( ( category: Category ): boolean => sysId === category.id )
    }

    function categoryBySlug( slug: string ): Category | undefined {
        return allCategories.value.find( ( category: Category ): boolean => slug === category.slug );
    }

    return {
        // ACTIONS
        categoryById,
        categoryBySlug,
        initialize,
        reset,

        // GETTERS
        allCategories
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useCategoriesStore, import.meta.hot ) );
}
